import { isFunction } from 'lodash'

const PREVENT_DEFAULT = 'preventDefault'
const EVENT_STOP_PROPAGATION = 'stopPropagation'

/**
 * Check if we have a valid browser event object
 * with the event function needed to be called.
 *
 * @private
 * @param {Object} event - The current event object
 * @param {Function} functionToCall - The event function to be checked
 * @returns {boolean} Whether this is a valid event with corresponding function
 */
function isValidEvent(event, functionToCall) {
    return event && isFunction(event[functionToCall])
}

/**
 * Stop event default action.
 *
 * @param {Object} event - The current event object
 * @returns {void}
 */
function eventPreventDefault(event) {
    if (isValidEvent(event, PREVENT_DEFAULT)) {
        event[PREVENT_DEFAULT]()
    }
}

/**
 * Stop event bubbling.
 *
 * @param {Object} event - The current event object
 * @returns {void}
 */
function eventStopPropagation(event) {
    if (isValidEvent(event, EVENT_STOP_PROPAGATION)) {
        event[EVENT_STOP_PROPAGATION]()
    }
}

/**
 * Create an "event-like" object structure with a given value
 * to be able to trigger custom event data.
 *
 * @param {*} value - The value to be added
 * @returns {Object} The event-like object
 */
function createEventTargetValue(value) {
    return {
        target: {
            value
        }
    }
}

/**
 * Stop event bubbling.
 *
 * @param {Object} event - The current event object
 * @param {string} [fallback=''] - The fallback if there is no value
 * @returns {*} The found value
 */
function getEventTargetValue(event, fallback = '') {
    return event?.target?.value !== undefined ? event.target.value : fallback
}

/**
 * wrap callback and stop propagation & default.
 *
 * @param {Object} event - The current event object
 * @param {string} [fallback=''] - The fallback if there is no value
 * @returns {*} The found value
 */
function preventBubbling(callback) {
    return e => {
        eventStopPropagation(e)
        eventPreventDefault(e)
        return isFunction(callback) && callback(e)
    }
}

export { eventPreventDefault, eventStopPropagation, createEventTargetValue, getEventTargetValue, preventBubbling }
